import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { type JSX, useCallback, useEffect, useState } from 'react';
import { generatePath } from 'react-router';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { Text } from 'Components/Text/Text';
import { COMPLIANCE_REQUIREMENTS_NO_ACTIVITY } from 'Config/Errors';
import { COMPLIANCE_REQUIREMENT_LISTING } from 'Config/PathTemplates';
import { AggregationMode } from 'Models/ComplianceRequirements';
import { ComplianceHistoryFFIECCAT, ComplianceSummary, ComplianceSummarySnapshot, MonthNumber } from 'Models/Dashboards';
import { OptionType } from 'Models/Types/GlobalType';

import { stringToComplianceModalType } from '../OperationalControlsDashboard.helpers';
import { ComplianceRequirementDisplayInformation, ComplianceRequirementDisplayModal } from './ComplianceRequirementDisplayModal/ComplianceRequirementDisplayModal';
import { ComplianceRequirementSummary } from './ComplianceRequirementSummary/ComplianceRequirementSummary';
import styles from './ComplianceRequirements.module.css';
import { FFIECRequirementSummary } from './FFIECRequirementSummary/FFIECRequirementSummary';

export interface ComplianceRequirementsProps {
    complianceSummaries?: ComplianceSummary[];
    complianceSummaryError?: Error;
    api: DashboardApi;
}

enum Modals {
    NONE,
    DISPLAY_REQUIREMENTS,
}

export const ComplianceRequirements = (props: ComplianceRequirementsProps): JSX.Element => {
    const { getComplianceAggregationMode, getComplianceMaturityStatusOptions } = useCachedData();
    const [complianceSummariesWithActivity, setComplianceSummariesWithActivity] = useState<ComplianceSummary[]>([]);
    const [complianceHistoryFFIECCAT, setComplianceHistoryFFIECCAT] = useState<ComplianceHistoryFFIECCAT>(); // This is basically the FFIEC CAT version of ComplianceSummarySnapshot.
    const [complianceSummarySnapshotsExceptFFIECCAT, setComplianceSummarySnapshotsExceptFFIECCAT] = useState<ComplianceSummarySnapshot[]>();
    const [selectedRegulation, setSelectedRegulation] = useState<string>();
    const [displayModalProps, setDisplayModalProps] = useState<ComplianceRequirementDisplayInformation>();
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [targetMaturity, setTargetMaturity] = useState<string>(''); // This will be set to the company's target maturity once it is retrieved.
    const [errorMessage, setErrorMessage] = useState<string>();

    const createRegulationOptions = (complianceSummaries: ComplianceSummary[]): OptionType[] => {
        return complianceSummaries.map((complianceSummary) => {
            return {
                label: complianceSummary.regulation,
                value: complianceSummary.regulation,
            };
        });
    };

    useEffect(() => {
        const getTargetMaturity = async () => {
            try {
                const response = await props.api.getTargetMaturity();
                setTargetMaturity(response.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };

        getTargetMaturity();
    }, [props.api]);

    // Get the specified Regulation's data from the API.
    const getRegulation = useCallback(
        (regulationName: string) => {
            const getRegulationData = async () => {
                try {
                    if (getComplianceAggregationMode(regulationName) === AggregationMode.FFIEC_CAT) {
                        if (targetMaturity) {
                            const ffiecSummary = await props.api.getComplianceHistorySummaryFFIECCAT(regulationName, targetMaturity);
                            setComplianceHistoryFFIECCAT(ffiecSummary.data);
                            setComplianceSummarySnapshotsExceptFFIECCAT(undefined);
                        }
                    } else {
                        const complianceHistorySummary = await props.api.getComplianceHistorySummary(regulationName);
                        setComplianceSummarySnapshotsExceptFFIECCAT(complianceHistorySummary.data);
                        setComplianceHistoryFFIECCAT(undefined);
                    }
                } catch (err) {
                    setErrorMessage(err.message);
                }
            };
            getRegulationData();
        },
        [getComplianceAggregationMode, props.api, targetMaturity]
    );

    // Filter Regulations so we only render those that have any activity.
    // If there are no Regulations with activity, show an "error" (explanation) message.
    useEffect(() => {
        let complianceSummariesWithActivity: ComplianceSummary[];
        if (!props.complianceSummaries || props.complianceSummaries.length === 0) {
            complianceSummariesWithActivity = [];
        } else {
            complianceSummariesWithActivity = props.complianceSummaries.filter((complianceSummary) => {
                const totalActiveRequirements = complianceSummary.awaiting_assessments_count + complianceSummary.met_count + complianceSummary.not_met_count;
                return totalActiveRequirements > 0;
            });
        }

        if (complianceSummariesWithActivity.length === 0) {
            setErrorMessage(COMPLIANCE_REQUIREMENTS_NO_ACTIVITY);
            setSelectedRegulation(undefined);
            setComplianceHistoryFFIECCAT(undefined);
            setComplianceSummarySnapshotsExceptFFIECCAT(undefined);
        } else {
            setErrorMessage(undefined);
            setComplianceSummariesWithActivity(complianceSummariesWithActivity);

            const regulationStillAvailable = selectedRegulation && complianceSummariesWithActivity.some((complianceSummary) => complianceSummary.regulation === selectedRegulation);
            if (!regulationStillAvailable) {
                setSelectedRegulation(complianceSummariesWithActivity[0].regulation);
            }
        }
    }, [props.complianceSummaries, selectedRegulation]);

    // Fetch selected Regulation data whenever selection changes.
    // For FFIEC CAT, this also runs again when target maturity changes.
    useEffect(() => {
        if (selectedRegulation) {
            getRegulation(selectedRegulation);
        }
    }, [selectedRegulation, getRegulation]);

    // When the selected Regulation changes, fetch its data.
    const handleSelectChange = (value: ChangeEventType): void => {
        setSelectedRegulation(value as string);
    };

    const handleSelectTargetMaturityChange = (value: ChangeEventType): void => {
        setTargetMaturity(value as string);
    };

    const handleChartClick = (item: string): void => {
        const clickedItem = item.split('|');
        const requirementClicked = clickedItem[1].slice(1);
        const dateClicked = moment(clickedItem[0], 'MMM YY ');
        if (stringToComplianceModalType(requirementClicked)) {
            const regulationTypeSelected = stringToComplianceModalType(requirementClicked);
            setDisplayModalProps({ regulationName: selectedRegulation!, regulationTypeSelected: regulationTypeSelected!, timeFrame: { month: (dateClicked.month() + 1) as MonthNumber, year: dateClicked.year() }, targetMaturity: getComplianceAggregationMode(selectedRegulation!) === AggregationMode.FFIEC_CAT ? targetMaturity : undefined });
            setModalState(Modals.DISPLAY_REQUIREMENTS);
        }
    };

    const handleProgressBarClicked = (requirementType: string) => {
        const dateClicked = moment();
        if (stringToComplianceModalType(requirementType)) {
            const regulationTypeSelected = stringToComplianceModalType(requirementType);
            setDisplayModalProps({ regulationName: selectedRegulation!, regulationTypeSelected: regulationTypeSelected!, timeFrame: { month: (dateClicked.month() + 1) as MonthNumber, year: dateClicked.year() }, targetMaturity: getComplianceAggregationMode(selectedRegulation!) === AggregationMode.FFIEC_CAT ? targetMaturity : undefined });
            setModalState(Modals.DISPLAY_REQUIREMENTS);
        }
    };

    // The parent component received an error when querying the API.
    if (props.complianceSummaryError) {
        return (
            <Text color="white" variant="Header4">
                {props.complianceSummaryError.message}
            </Text>
        );
    }

    // This component received an error when querying the API.
    else if (errorMessage) {
        return (
            <Text color="white" variant="Header4">
                {errorMessage}
            </Text>
        );
    }

    // Everything we need loaded successfully.
    else if (selectedRegulation && (complianceSummarySnapshotsExceptFFIECCAT || complianceHistoryFFIECCAT)) {
        return (
            <>
                {modalState === Modals.DISPLAY_REQUIREMENTS && displayModalProps && <ComplianceRequirementDisplayModal api={props.api} hideModal={() => setModalState(Modals.NONE)} regulationInformation={displayModalProps} />}
                <div className={styles.header}>
                    <Link size="lg" to={`${generatePath(COMPLIANCE_REQUIREMENT_LISTING)}#${selectedRegulation}`}>
                        {selectedRegulation}
                    </Link>
                    <FormFieldSelectDark formFieldId="regulation" formFieldLabel="Regulation" options={createRegulationOptions(complianceSummariesWithActivity)} selectedOption={selectedRegulation} handleChange={handleSelectChange} />
                </div>
                {complianceHistoryFFIECCAT && (
                    <div className={styles.targetMaturitySelectContainer}>
                        <div className={styles.targetMaturitySelect}>
                            <FormFieldSelectDark formFieldId="targetMaturity" formFieldLabel="Target Maturity" options={getComplianceMaturityStatusOptions(selectedRegulation!)} selectedOption={targetMaturity} handleChange={handleSelectTargetMaturityChange} />
                        </div>
                    </div>
                )}
                {complianceSummarySnapshotsExceptFFIECCAT && <ComplianceRequirementSummary complianceSummary={complianceSummariesWithActivity.find((complianceSummary) => complianceSummary.regulation === selectedRegulation)!} complianceSummarySnapshots={complianceSummarySnapshotsExceptFFIECCAT} handleChartClick={handleChartClick} handleProgressBarClicked={handleProgressBarClicked} />}
                {complianceHistoryFFIECCAT && <FFIECRequirementSummary complianceSummarySnapshots={complianceHistoryFFIECCAT} handleChartClick={handleChartClick} handleProgressBarClicked={handleProgressBarClicked} />}
            </>
        );
    }

    // Things are still loading.
    else {
        return (
            <>
                <Skeleton>
                    <Link size="lg" to={generatePath(COMPLIANCE_REQUIREMENT_LISTING)}>
                        Loading Regulation
                    </Link>
                    <FormFieldSelectDark formFieldId="regulation" formFieldLabel="Regulation" />
                </Skeleton>
                <Skeleton variant="rounded" width={'100%'} height={300} />
            </>
        );
    }
};
